/**
 * SafeMarkdown — a deliberately tiny markdown renderer for policy text.
 * Supports headings (#..###), paragraphs, bullet and numbered lists, and
 * **bold** / *emphasis* / _emphasis_. Everything is built as React elements
 * (text is always escaped by React); there is no HTML pass-through and no
 * dangerouslySetInnerHTML. Links, images and raw HTML render as plain text.
 */
import type { ReactNode } from 'react';

type Block =
  | { kind: 'h'; level: 1 | 2 | 3; text: string }
  | { kind: 'p'; text: string }
  | { kind: 'ul' | 'ol'; items: string[] };

export function parseMarkdownBlocks(src: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: { kind: 'ul' | 'ol'; items: string[] } | null = null;
  const flushPara = () => { if (para.length) { blocks.push({ kind: 'p', text: para.join(' ') }); para = []; } };
  const flushList = () => { if (list) { blocks.push(list); list = null; } };
  for (const raw of src.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd();
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    const ul = /^\s*[-*+]\s+(.*)$/.exec(line);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (!line.trim()) { flushPara(); flushList(); continue; }
    if (h) { flushPara(); flushList(); blocks.push({ kind: 'h', level: h[1]!.length as 1 | 2 | 3, text: h[2]! }); continue; }
    if (ul || ol) {
      flushPara();
      const kind = ul ? 'ul' : 'ol';
      if (!list || list.kind !== kind) { flushList(); list = { kind, items: [] }; }
      list.items.push((ul ?? ol)![1]!);
      continue;
    }
    flushList();
    para.push(line.trim());
  }
  flushPara(); flushList();
  return blocks;
}

/** **bold**, *em*, _em_ → elements; everything else stays text. */
export function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const rx = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|_[^_\s][^_]*_)/g;
  let last = 0, m: RegExpExecArray | null, i = 0;
  while ((m = rx.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    out.push(tok.startsWith('**') ? <strong key={i++}>{tok.slice(2, -2)}</strong> : <em key={i++}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** `headingBase` is the level of the first markdown heading (e.g. 3 inside a drawer whose title is an h2). */
export function SafeMarkdown({ source, headingBase = 3 }: { source: string; headingBase?: 3 | 4 }) {
  return <>{parseMarkdownBlocks(source).map((b, i) => {
    if (b.kind === 'h') {
      const level = Math.min(6, headingBase + b.level - 1);
      const Tag = `h${level}` as 'h3' | 'h4' | 'h5' | 'h6';
      return <Tag key={i}>{renderInline(b.text)}</Tag>;
    }
    if (b.kind === 'p') return <p key={i}>{renderInline(b.text)}</p>;
    const List = b.kind;
    return <List key={i}>{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</List>;
  })}</>;
}
